<template>
  <Dialog v-model:visible="loggerState.visible" modal :header="headerText" @hide="closeLogger">
    <div class="space-y-4">
      <Dropdown
        v-model="loggerState.direction"
        :options="['outgoing', 'incoming']"
        placeholder="Select direction"
        class="w-full"
      />

      <!-- Add People Selector when organization is selected with clear option -->
      <div v-if="loggerState.organizationId && organizationPeople.length > 0" class="w-full">
        <Dropdown
          v-model="loggerState.personId"
          :options="organizationPeople"
          optionLabel="fullName"
          optionValue="id"
          placeholder="Select contact person (optional)"
          class="w-full"
          :showClear="true"
        />
      </div>

      <Textarea
        v-model="noteInput"
        autoResize
        rows="3"
        class="w-full"
        placeholder="Enter call notes..."
      />

      <MultiSelect
        v-model="selectedTagIds"
        :options="tagStore.tags"
        optionLabel="label"
        optionValue="id"
        placeholder="Select tags"
        display="chip"
        class="w-full"
      />

      <div class="flex justify-end gap-2">
        <Button label="Cancel" @click="closeLogger" text />
        <Button
          label="Save"
          icon="pi pi-save"
          @click="logCall"
          :disabled="!noteInput.trim() || (!loggerState.personId && !loggerState.organizationId)"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import Dialog from 'primevue/dialog'
import Textarea from 'primevue/textarea'
import MultiSelect from 'primevue/multiselect'
import Button from 'primevue/button'
import Dropdown from 'primevue/dropdown'
import { useCallLogTagStore } from '@/stores/callLogTagStore'
import { loggerState, useCallLogger } from '@/composables/useCallLogger'
import { usePersonStore } from '@/stores/v2/person'
import { useOrganizationStore } from '@/stores/v2/organization'
import { createPerson } from '@/factories/personFactory'
import { type Person } from '@/types/Person'
import { useCallLogService } from '@/services/callLogService'

const { closeLogger } = useCallLogger()
const noteInput = ref('')
const selectedTagIds = ref<string[]>([])

const tagStore = useCallLogTagStore()
const callLogService = useCallLogService()
const personStore = usePersonStore()
const organizationStore = useOrganizationStore()

const person = ref(createPerson())
const organization = ref<{ name: string } | null>(null)
const organizationPeople = ref<(Person & { fullName: string })[]>([])

const headerText = computed(() => {
  const hasPerson = !!person.value?.firstName
  const hasOrg = !!organization.value?.name

  if (hasPerson && hasOrg) {
    return `Log call for ${person.value.firstName} ${person.value.lastName || ''} at ${organization.value!.name}`
  } else if (hasPerson) {
    return `Log call for ${person.value.firstName} ${person.value.lastName || ''}`
  } else if (hasOrg) {
    return `Log call for ${organization.value!.name}`
  } else {
    return 'Log Call'
  }
})

watch(
  () => loggerState.visible,
  async (val) => {
    if (val) {
      tagStore.load()

      // Load person data if personId is available
      if (loggerState.personId) {
        try {
          const personData = await personStore.get(loggerState.personId)
          if (personData) {
            person.value = personData
          }
        } catch (error) {
          console.error('Error loading person data', error)
        }
      }

      // Load organization data if organizationId is available
      if (loggerState.organizationId) {
        try {
          const orgData = await organizationStore.get(loggerState.organizationId)
          if (orgData) {
            organization.value = orgData

            // Load people associated with this organization via search API
            try {
              const search = await personStore.search()
              const people = await search.byOrganization(loggerState.organizationId).run()
              organizationPeople.value = people.map((p) => ({
                ...p,
                fullName: `${p.firstName} ${p.lastName || ''}`.trim(),
              }))
            } catch (e) {
              console.error('Failed to load organization people:', e)
              organizationPeople.value = []
            }
          }
        } catch (error) {
          console.error('Error loading organization data', error)
        }
      }
    } else {
      // Reset data when dialog closes
      person.value = createPerson()
      organization.value = null
      organizationPeople.value = []
      loggerState.personId = undefined
    }
  },
)

// Watch for changes in personId to update the person data
watch(
  () => loggerState.personId,
  async (newPersonId) => {
    if (newPersonId) {
      try {
        const personData = await personStore.get(newPersonId)
        if (personData) {
          person.value = personData
        }
      } catch (error) {
        console.error('Error loading person data', error)
      }
    } else {
      person.value = createPerson()
    }
  },
)

async function logCall() {
  await callLogService.addCallLog({
    timestamp: new Date(),
    direction: loggerState.direction,
    notes: noteInput.value.trim(),
    personId: loggerState.personId ?? undefined,
    organizationId: loggerState.organizationId ?? undefined,
    tags: selectedTagIds.value,
    createdAt: new Date(),
    updatedAt: new Date(),
  })

  loggerState.onSubmitted?.()
  closeLogger()
  noteInput.value = ''
  selectedTagIds.value = []
}
</script>

<style scoped>
:deep(.p-dialog-content) {
  padding-bottom: 0;
}
</style>
